import * as THREE from 'three';

interface DamagePopup {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  age: number;
  lifetime: number;
  baseScale: number;
}

/**
 * Short-lived, world-space hit values for WebXR. The desktop DOM popup cannot
 * be composited into an immersive session, so confirmed server damage needs a
 * real scene object at the impact point.
 */
export class WorldDamageNumbers {
  private popups: DamagePopup[] = [];
  private textures = new Map<string, THREE.CanvasTexture>();

  constructor(private parent: THREE.Object3D) {}

  spawn(point: THREE.Vector3, damage: number, critical = false): void {
    const value = Math.max(0, Math.round(damage));
    const key = `${value}:${critical ? 'critical' : 'normal'}`;
    const texture = this.textures.get(key) ?? this.createTexture(value, critical);
    this.textures.set(key, texture);

    const material = new THREE.SpriteMaterial({
      map: texture,
      color: 0xffffff,
      transparent: true,
      opacity: 1,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.position.copy(point).add(new THREE.Vector3(0, critical ? 1.2 : 0.85, 0));
    // Enemies commonly sit 30–60 metres ahead; metre-sized text disappears
    // at that distance in a headset even though it looks adequate on desktop.
    const baseScale = critical ? 3.1 : 2.55;
    sprite.scale.set(baseScale, baseScale * 0.42, 1);
    sprite.renderOrder = 80;
    this.parent.add(sprite);
    this.popups.push({ sprite, material, age: 0, lifetime: critical ? 1.05 : 0.82, baseScale });
  }

  update(dt: number): void {
    for (let i = this.popups.length - 1; i >= 0; i--) {
      const popup = this.popups[i];
      popup.age += dt;
      const t = Math.min(1, popup.age / popup.lifetime);
      popup.sprite.position.y += dt * (0.62 + t * 0.35);
      const punch = t < 0.16 ? 0.78 + (t / 0.16) * 0.34 : 1.12 - (t - 0.16) * 0.14;
      popup.sprite.scale.set(popup.baseScale * punch, popup.baseScale * 0.42 * punch, 1);
      popup.material.opacity = t < 0.62 ? 1 : 1 - (t - 0.62) / 0.38;
      if (t >= 1) {
        popup.sprite.removeFromParent();
        popup.material.dispose();
        this.popups.splice(i, 1);
      }
    }
  }

  clear(): void {
    for (const popup of this.popups) {
      popup.sprite.removeFromParent();
      popup.material.dispose();
    }
    this.popups.length = 0;
  }

  private createTexture(value: number, critical: boolean): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 96;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = "italic 900 58px 'Audiowide', 'Courier New', monospace";
    ctx.lineJoin = 'round';
    ctx.lineWidth = 13;
    ctx.strokeStyle = 'rgba(3, 5, 12, 0.92)';
    ctx.strokeText(`-${value}`, 128, 49);
    ctx.shadowBlur = 16;
    ctx.shadowColor = critical ? '#ff2d95' : '#2ee6d6';
    ctx.fillStyle = critical ? '#ffe066' : '#ffffff';
    ctx.fillText(`-${value}`, 128, 49);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    return texture;
  }
}
